import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import useAuthStore from '../stores/authStore';

// Tipos
export interface Company {
  id: string;
  name: string;
  slug: string;
  description?: string;
  plan?: string;
  is_active: boolean;
  settings?: Record<string, any>;
  role?: string;
  permissions?: {
    can_edit: boolean;
    can_view: boolean;
    is_admin: boolean;
    is_owner: boolean;
    custom: Record<string, any>;
  };
  inventory_mode?: 'disabled' | 'managed' | 'hybrid';
  warehouse_mode?: 'none' | 'single' | 'multi';
  inventory_settings?: {
    auto_reserve?: boolean;
    low_stock_alert?: boolean;
    alert_threshold?: number;
    sync_interval?: number;
  };
  has_inventory_enabled?: boolean;
  has_warehouses_enabled?: boolean;
  created_at: string;
  updated_at: string;
}

interface CompanyContextType {
  currentCompany: Company | null;
  companies: Company[];
  isLoading: boolean;
  error: string | null;
  switchCompany: (companyId: string) => Promise<void>;
  refreshCompanies: () => Promise<void>;
  refreshCompany: () => Promise<void>;
}

// Context
const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

// Hook para usar o contexto
export const useCompany = (): CompanyContextType => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany deve ser usado dentro de um CompanyProvider');
  }
  return context;
};

// Provider props
interface CompanyProviderProps {
  children: ReactNode;
}

// Provider component
export const CompanyProvider: React.FC<CompanyProviderProps> = ({ children }) => {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const [currentCompany, setCurrentCompany] = useState<Company | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Buscar dados completos da empresa atual
  const fetchCurrentCompany = async (): Promise<Company | null> => {
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('access_token');
      if (!token) {
        console.warn('Token não encontrado');
        return null;
      }

      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const companyId = localStorage.getItem('current_company_id');
      const headers: Record<string, string> = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };
      if (companyId) {
        headers['X-Company-Id'] = companyId;
      }
      const response = await fetch(`${baseURL}/api/v1/companies/current/`, { headers });

      if (!response.ok) {
        if (response.status === 404 && companyId) {
          localStorage.removeItem('current_company_id');

          const retryResponse = await fetch(`${baseURL}/api/v1/companies/current/`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          if (!retryResponse.ok) {
            throw new Error(`Erro ao buscar empresa atual: ${retryResponse.status}`);
          }

          const retryData = await retryResponse.json();
          return retryData.success && retryData.data ? retryData.data : null;
        }

        throw new Error(`Erro ao buscar empresa atual: ${response.status}`);
      }

      const data = await response.json();
      return data.success && data.data ? data.data : null;
    } catch (err) {
      console.error('Erro ao buscar empresa atual:', err);
      throw err;
    }
  };

  // Buscar empresas da API
  const fetchCompanies = async (): Promise<Company[]> => {
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('access_token');
      if (!token) {
        console.warn('Token não encontrado');
        return [];
      }

      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${baseURL}/api/v1/companies/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Erro ao buscar empresas: ${response.status}`);
      }

      const data = await response.json();
      // Ajustar para estrutura correta da API
      if (data.results) {
        return data.results;
      }
      return data.success ? data.data : [];
    } catch (err) {
      console.error('Erro ao buscar empresas:', err);
      throw err;
    }
  };

  // Atualizar dados da empresa atual
  const refreshCompany = async () => {
    try {
      const companyData = await fetchCurrentCompany();
      if (companyData) {
        setCurrentCompany(companyData);
        localStorage.setItem('current_company_id', companyData.id);
      }
    } catch (err) {
      console.error('Erro ao atualizar empresa atual:', err);
      setError(err instanceof Error ? err.message : 'Erro ao atualizar empresa');
    }
  };

  // Carregar empresas
  const refreshCompanies = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const companiesData = await fetchCompanies();
      setCompanies(companiesData);
      
      // Buscar dados completos da empresa atual
      const currentCompanyData = await fetchCurrentCompany();
      if (currentCompanyData) {
        setCurrentCompany(currentCompanyData);
        localStorage.setItem('current_company_id', currentCompanyData.id);
      } else if (!currentCompany && companiesData.length > 0) {
        // Se não há empresa atual, definir a primeira como padrão
        const defaultCompany = companiesData.find(c => c.is_active) || companiesData[0];
        setCurrentCompany(defaultCompany);
        localStorage.setItem('current_company_id', defaultCompany.id);
      }
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
    } finally {
      setIsLoading(false);
    }
  };

  // Trocar empresa atual
  const switchCompany = async (companyId: string) => {
    try {
      const company = companies.find(c => c.id === companyId);
      if (!company) {
        throw new Error('Empresa não encontrada');
      }

      // Definir no localStorage primeiro para que o interceptor use o novo ID
      localStorage.setItem('current_company_id', companyId);

      // Buscar dados completos da empresa via API (com X-Company-Id ativo)
      const fullCompanyData = await fetchCurrentCompany();
      setCurrentCompany(fullCompanyData || company);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao trocar empresa');
      throw err;
    }
  };

  // Carregar empresas quando o usuario faz login
  useEffect(() => {
    if (!isAuthenticated) {
      setCompanies([]);
      setCurrentCompany(null);
      setIsLoading(false);
      return;
    }

    const savedCompanyId = localStorage.getItem('current_company_id');

    const initializeCompany = async () => {
      await refreshCompanies();

      // Se existe empresa salva, tentar definir como atual
      if (savedCompanyId && companies.length > 0) {
        const savedCompany = companies.find(c => c.id === savedCompanyId);
        if (savedCompany) {
          setCurrentCompany(savedCompany);
        }
      }
    };

    initializeCompany();
  }, [isAuthenticated]);

  const value: CompanyContextType = {
    currentCompany,
    companies,
    isLoading,
    error,
    switchCompany,
    refreshCompanies,
    refreshCompany,
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};

export default CompanyContext;
